<template>

    <div class="h-screen flex flex-col overflow-hidden bg-gray-50">
        <Toast></Toast>

        <!-- Full Width Header -->
        <header 
            class="relative z-20 flex-shrink-0 h-16 flex items-center"
            :style="{ backgroundColor: $page.props.branding?.navbar_color || '#2f3441', color: $page.props.branding?.navbar_text_color || '#ffffff' }"
        >
            <!-- Logo Section (Aligned with Sidebar width) -->
            <div class="hidden xl:flex items-center w-64 pl-6 h-full">
                <Link href="/">
                    <img v-if="$page.props.branding?.logo_url" class="h-10 w-auto" :src="$page.props.branding.logo_url" alt="Agency Logo" />
                    <img v-else class="h-10 w-auto" src="../../image/immivo.svg" alt="immivo" />
                </Link>
            </div>

            <!-- Mobile Menu Button -->
            <div class="flex items-center h-full">
                <button
                    class="px-4 text-gray-500 h-full focus:outline-none md:hidden"
                    type="button"
                    @click="mobileMenuOpen = true"
                >
                    <span class="sr-only">Open sidebar</span>
                    <MenuAlt2Icon aria-hidden="true" class="h-6 w-6 text-white" />
                </button>
            </div>

            <!-- Navbar Content -->
            <div class="flex-1 flex justify-between px-4 sm:px-6 h-full items-center">
                <div class="flex-1 flex items-center justify-center lg:justify-between">
                    <!-- Active Project -->
                    <div class="flex items-center">
                        <span class="mr-2 opacity-70 text-[11px] uppercase tracking-widest hidden sm:block font-bold">{{
                            $t("apartments.projectNameHeader")
                        }}</span>
                        <span class="text-sm font-medium">{{ project.name }}</span>
                    </div>

                    <!-- Agency Name (Center) -->
                    <div v-if="$page.props.isSuperAdmin && $page.props.user.currentAgencyName" class="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span class="text-xl font-bold uppercase tracking-wider pointer-events-auto">
                            {{ $page.props.user.currentAgencyName }}
                        </span>
                    </div>
                </div>

                <!-- Top Right Menu -->
                <div class="ml-2 pl-4 flex items-center space-x-4 sm:ml-6 sm:space-x-6">
                    <button
                        class="flex p-1.5 rounded-full items-center justify-center hover:bg-white/10 transition-colors focus:outline-none"
                        type="button"
                        @click="navToApartments()"
                    >
                        <ChevronUpIcon aria-hidden="true" class="h-6 w-6" />
                    </button>

                    <user-navigation-dropdown :user="$page.props.user" />
                </div>
            </div>
        </header>

        <!-- Body Area (Sidebar + Content) -->
        <div class="flex-1 flex overflow-hidden">
            <apartment-navigation
                :apartment="apartment"
                :buyers="buyers"
                :mobile-menu-open="mobileMenuOpen"
                :topic="topic"
                :topics="topics"
                :show-apartment-route-string="'clients.apartments.show'"
                :show-topic-route-string="'clients.apartments.topics.show'"
                @closeMobileMenu="mobileMenuOpen = false"
            />

            <!-- Main Scrollable Content -->
            <main class="flex-1 overflow-y-auto focus:outline-none bg-white">
                <div class="py-6">
                    <slot></slot>

                    <footer class="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 lg:max-w-7xl mt-12 mb-8">
                        <div class="pt-6 border-t border-gray-200">
                            <div class="flex justify-between items-center text-gray-400 text-sm">
                                <div>&copy; {{ new Date().getFullYear() }} IMMIVO</div>
                                <div class="flex items-center">
                                    <a
                                        :href="`mailto:${$page.props.feedback_email}?subject=IMMIVO%20Feedback`"
                                        class="hover:text-minplan-blue group flex items-center transition-colors"
                                    >
                                        <ChatAlt2Icon class="h-5 w-5 mr-2" />
                                        <span>{{ $t("dialogs.sendFeedback") }}</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </footer>
                </div>

                <ComponentModal @reloadTopics="loadTopics" />
            </main>
        </div>
    </div>
</template>

<script>
import { ref } from "vue";
import { io } from "socket.io-client";
import { ChevronUpIcon, MenuAlt2Icon, SearchIcon, XIcon, ChatAlt2Icon } from "@heroicons/vue/outline";
import { CheckIcon } from "@heroicons/vue/solid";
import { Link } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import ComponentModal from "@/Shared/Components/ComponentModal";
import Toast from "@/Shared/Components/Toast";
import UserNavigationDropdown from "@/Shared/Components/UserNavigationDropdown";
import ApartmentNavigation from "@/Layouts/Components/ApartmentNavigation";
import {
    buildChatNotificationEventKey,
    fetchChatSocketContext,
    getChatNotificationTargets,
    getChatSocketUrl,
    isRelevantChatNotification,
    joinChatNotificationRooms,
    resolveChatNotificationPayload,
} from "@/Shared/Utils/chatNotifications";

export default {
    props: ["apartment", "buyers", "selectedTopic", "project", "topic", "event"],
    components: {
        MenuAlt2Icon,
        XIcon,
        SearchIcon,
        ChevronUpIcon,
        CheckIcon,
        Link,
        ComponentModal,
        Toast,
        UserNavigationDropdown,
        ApartmentNavigation,
        ChatAlt2Icon
    },
    setup() {
        let topics = ref(null);
        const mobileMenuOpen = ref(false);
        return {
            mobileMenuOpen,
            topics
        };
    },
    data() {
        return {
            removeStartEventListener: null,
            isMuted: localStorage.getItem('chat_muted') === 'true',
            recentChatNotificationEvents: {},
            mutedThreadIds: [],
            globalSocket: null,
            removeSuccessEventListener: null,
            socketAuthRefreshInProgress: false,
            socketInitPromise: null,
        };
    },
    async created() {
        await this.loadTopics(this.apartment.uuid);
    },
    mounted() {
        this.initGlobalSocket();
        this.removeSuccessEventListener = Inertia.on("success", () => {
            this.refreshGlobalSocket();
        });
    },
    watch: {
        apartment: {
            immediate: true,
            handler(newVal) {
                if (newVal && this.globalSocket && this.globalSocket.connected) {
                    this.rejoinThreads(newVal);
                }
            }
        },
        "$page.props.chatNotifications": {
            deep: true,
            handler() {
                this.refreshGlobalSocket();
            },
        }
    },
    beforeUnmount() {
        if (this.removeSuccessEventListener) {
            this.removeSuccessEventListener();
            this.removeSuccessEventListener = null;
        }
        if (this.globalSocket) {
            this.globalSocket.disconnect();
            this.globalSocket = null;
        }
    },
    methods: {
        forEachBuyerList(callback) {
            const seen = new Set();
            [this.buyers, this.$page.props.buyers].forEach((list) => {
                if (!Array.isArray(list) || seen.has(list)) return;
                seen.add(list);
                callback(list);
            });
        },
        shouldHandleChatNotificationEvent(evt) {
            const now = Date.now();
            const ttl = 3000;
            const key = buildChatNotificationEventKey(evt);

            Object.keys(this.recentChatNotificationEvents).forEach((existingKey) => {
                if ((now - this.recentChatNotificationEvents[existingKey]) > ttl) {
                    delete this.recentChatNotificationEvents[existingKey];
                }
            });

            if (this.recentChatNotificationEvents[key]) {
                return false;
            }

            this.recentChatNotificationEvents[key] = now;
            return true;
        },
        refreshGlobalSocketRooms() {
            if (!this.globalSocket || !this.globalSocket.connected) return;

            this.globalSocket.emit("user.join", { user_id: this.$page.props.user.id });

            const isManager = `${this.$page.props.user.currentAgencyRole || ""}`.toLowerCase() === "manager";
            const canJoinApartment = !isManager || this.apartment?.is_assigned_to_me === true;
            joinChatNotificationRooms(this.globalSocket, this.$page.props, {
                apartments: canJoinApartment ? [this.apartment] : [],
            });
        },
        async refreshSocketAuth() {
            if (!this.globalSocket || this.socketAuthRefreshInProgress) return;

            this.socketAuthRefreshInProgress = true;

            try {
                const { token, mutedThreadIds } = await fetchChatSocketContext(this.$page.props);
                if (!token) return;

                this.mutedThreadIds = mutedThreadIds;
                this.globalSocket.auth = { token };

                if (!this.globalSocket.connected) {
                    this.globalSocket.connect();
                }
            } catch (error) {
                console.error("Client apartment socket auth refresh failed", error);
            } finally {
                this.socketAuthRefreshInProgress = false;
            }
        },
        async refreshGlobalSocket() {
            const chatTargets = getChatNotificationTargets(this.$page.props);
            if (!chatTargets.enabled) return;

            if (this.globalSocket) {
                if (this.globalSocket.connected) {
                    this.refreshGlobalSocketRooms();
                    return;
                }

                if (!this.globalSocket.active) {
                    await this.refreshSocketAuth();
                    return;
                }

                this.globalSocket.connect();
                return;
            }

            await this.initGlobalSocket();
        },
        async initGlobalSocket(forceReconnect = false) {
            if (this.socketInitPromise && !forceReconnect) {
                return this.socketInitPromise;
            }

            if (forceReconnect && this.globalSocket) {
                this.globalSocket.disconnect();
                this.globalSocket = null;
            }

            this.socketInitPromise = (async () => {
            try {
                const chatTargets = getChatNotificationTargets(this.$page.props);
                if (!chatTargets.enabled) return;

                const { token, mutedThreadIds } = await fetchChatSocketContext(this.$page.props);
                const socketUrl = getChatSocketUrl(this.$page.props);

                this.mutedThreadIds = mutedThreadIds;

                this.globalSocket = io(socketUrl, {
                    transports: ["websocket"],
                    auth: { token },
                    reconnection: true,
                    reconnectionAttempts: Infinity,
                    reconnectionDelay: 1000,
                });

                this.globalSocket.on("connect", () => {
                   this.refreshGlobalSocketRooms();
                });

                this.globalSocket.on("connect_error", () => {
                    this.refreshSocketAuth();
                });

                this.globalSocket.on("message.created", async (evt) => {
                    const threadId = Number(evt.thread_id || evt.threadId);
                    const senderId = Number(evt.sender_id || evt.senderId);
                    
                    if (senderId === Number(this.$page.props.user.id)) return;
                    if (!isRelevantChatNotification(this.$page.props, evt)) return;
                    if (!this.shouldHandleChatNotificationEvent(evt)) return;

                    const buyersList = this.buyers || this.$page.props.buyers || [];
                    const userRole = (this.$page.props.user.currentAgencyRole || this.$page.props.user.type || '').toLowerCase();
                    const isManager = ['manager', 'admin', 'advisor'].includes(userRole);
                    
                    const relevantBuyers = isManager 
                        ? buyersList 
                        : buyersList.filter(b => Number(b.user_id) === Number(this.$page.props.user.id));
                    
                    const explicitBuyerId = Number(evt.buyer_id || evt.buyerId);
                    if (explicitBuyerId) {
                        const targetBuyer = relevantBuyers.find(b => Number(b.id) === explicitBuyerId);
                        if (targetBuyer && !targetBuyer.chat_thread_id) {
                            targetBuyer.chat_thread_id = threadId;
                        }
                    } else if (!isManager) {
                        const targetThreadMatches = relevantBuyers.some(b => Number(b.chat_thread_id) === threadId);
                        if (!targetThreadMatches) {
                            for (const buyer of relevantBuyers) {
                                if (buyer.chat_thread_id) continue;
                                try {
                                    const base = this.$page.props.user.type === 'buyer' ? '/c' : '/a';
                                    const res = await window.axios.get(`${base}/apartments/${this.apartment.uuid}/buyers/${buyer.id}/chat/thread`);
                                    if (res.data?.thread?.id) {
                                        buyer.chat_thread_id = res.data.thread.id;
                                    }
                                } catch (e) {
                                    console.error("Failed fetching thread data", e);
                                }
                            }
                        }
                    }

                    const isRelevant = relevantBuyers.some(b => Number(b.chat_thread_id) === threadId);
                    if (!isManager && !isRelevant) return;

                    const isChatPage = this.route().current('clients.apartments.chat') || this.route().current('advisors.apartments.chat');
                    const params = new URLSearchParams(window.location.search);
                    const activeBuyerId = params.get('buyer');
                    const activeBuyer = relevantBuyers.find(b => Number(b.id) === Number(activeBuyerId || relevantBuyers[0]?.id));
                    let isCurrentChatThread = isChatPage && Number(activeBuyer?.chat_thread_id) === threadId;
                    const notification = await resolveChatNotificationPayload(this.$page.props, evt, this.route);
                    
                    if (isChatPage && !isCurrentChatThread && !isManager && !activeBuyerId) {
                        isCurrentChatThread = isRelevant;
                    }

                    if (!isChatPage || !isCurrentChatThread) {
                        this.$notify({
                            group: "success",
                            title: notification.title,
                            text: notification.text,
                            url: notification.url,
                        }, 4000);

                        const mutedThreads = JSON.parse(localStorage.getItem('chat_muted_threads') || '{}');
                        const isSpecificMuted = this.mutedThreadIds.includes(Number(threadId)) || !!mutedThreads[threadId];

                        if (!isSpecificMuted) {
                            const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3");
                            audio.play().catch(e => console.log('Audio play failed:', e));
                        }
                    }

                    const updateBuyers = (list) => {
                        if (!list) return;
                        let buyer = list.find(b => Number(b.chat_thread_id) === threadId);
                        if (!buyer && explicitBuyerId) {
                            buyer = list.find(b => Number(b.id) === explicitBuyerId);
                            if (buyer) buyer.chat_thread_id = threadId;
                        }
                        if (buyer) {
                            if (!isChatPage || !isCurrentChatThread) {
                                buyer.unread_count = (Number(buyer.unread_count) || 0) + 1;
                            }
                        }
                    };
                    this.forEachBuyerList(updateBuyers);
                });

                this.globalSocket.on("thread.read_changed", (evt) => {
                    if (Number(evt.user_id) !== Number(this.$page.props.user.id)) return;

                    const threadId = Number(evt.thread_id || evt.threadId);
                    const resetUnread = (list) => {
                        if (!list) return;
                        const buyer = list.find(b => Number(b.chat_thread_id) === threadId);
                        if (buyer) {
                           buyer.unread_count = 0;
                        }
                    };
                    this.forEachBuyerList(resetUnread);
                });

            } catch (e) {
                console.error("Global socket init failed", e);
            }
            })().finally(() => {
                this.socketInitPromise = null;
            });

            return this.socketInitPromise;
        },
        rejoinThreads(apartment) {
             if (!this.globalSocket || !apartment?.id) return;

             this.globalSocket.emit("apartment.join", { apartment_id: apartment.id });
        },
        async loadTopics(apartmentUUID) {
            const to_response = await fetch(this.route("clients.apartments.topics", { apartment: apartmentUUID }));
            this.topics = await to_response.json();
        },
        navToApartments() {
            Inertia.visit(this.route("clients.apartments.index"));
        }
    },
};
</script>
